import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Row,
  Container,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import { useLocation, useNavigate, Link } from "react-router-dom";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import PaymentTypeServices from "../../../services/OrderingServices/PaymentTypeService";
import PaymentReportService from "../../../services/OrderingServices/PaymentReportService";
import NumberWithCommas from "../../../pages/Pages/Starter/NumberWithCommas";
import CustomDataTable from "../../../Components/CustomTable/CustomTable";
import { formatDate } from "../../../Components/Common/FormatDate";
import CountUp from "react-countup";
import toast from "react-hot-toast";
import { exportToExcel } from "../../../utilis/exportToExcel";

// Helper to format date as YYYY-MM-DD
const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const PaymentReport = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [paymentTypeData, setPaymentTypeData] = useState([]);
  const [transactions, setTransactions] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({
    paymentTypeId: location.state?.paymentTypeId || null,
    startDate: null,
    endDate: null,
  });

  const breadcrumbItems = [
    { title: "Back To List", onClick: () => navigate(-1) },
  ];

  // Fetch payment types
  const fetchPaymentTypes = async () => {
    try {
      const types = await PaymentTypeServices.getList();
      setPaymentTypeData(types);
    } catch (error) {
      console.error("Error fetching payment types:", error);
    }
  };

  // Fetch transactions
  const fetchTransactions = async () => {
    const paymentTypeId = filters.paymentTypeId;
    const startDate = filters.startDate
      ? formatLocalDate(filters.startDate)
      : null;
    const endDate = filters.endDate ? formatLocalDate(filters.endDate) : null;

    try {
      const data = await PaymentReportService.getList(
        paymentTypeId,
        startDate,
        endDate,
      );
      const transformed = data.map((tx) => ({
        transactionId: tx.invoiceNumber,
        valueDate: tx.transactionDate,
        remarks: tx.remarks,
        transactionType: tx.debit > 0 ? "Debit" : "Credit",
        amount: tx.debit > 0 ? tx.debit : tx.credit,
        transactionBy: tx.transactionBy,
        paymentType:
          paymentTypeData.find((p) => p.id === tx.paymentTypeId)?.name || "",
      }));
      setTransactions(transformed);
    } catch (error) {
      console.error("Error fetching transactions:", error);
      setTransactions([]);
    }
  };

  // Initial setup: fetch payment types and transactions
  // Inside useEffect for initial setup
  useEffect(() => {
    fetchPaymentTypes();

    const today = new Date();
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(today.getMonth() - 1);

    setFilters((prev) => ({
      ...prev,
      startDate: oneMonthAgo,
      endDate: today,
    }));
  }, []);

  // Fetch transactions whenever filters change
  useEffect(() => {
    fetchTransactions();
  }, [
    filters.paymentTypeId,
    filters.startDate,
    filters.endDate,
    paymentTypeData,
  ]);

  const handleFilterChange = (value, key) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
  };

  // Summary card component
  const SummaryCard = ({ title, value, color, icon }) => (
    <Card className="shadow-sm border-0">
      <CardBody className="d-flex align-items-center justify-content-between">
        <div className="w-100">
          <h6 className="fw-semibold text-uppercase" style={{ color }}>
            {title}
          </h6>
          <div className="d-flex justify-content-between w-100">
            <h4 className="fs-2 fw-bold" style={{ color }}>
              <CountUp start={0} end={value} duration={0} separator="," />
            </h4>
            <div
              className="rounded-circle d-flex align-items-center justify-content-center"
              style={{
                backgroundColor: color + "20",
                color,
                width: "36px",
                height: "36px",
              }}
            >
              <i className={`${icon} fs-5`} />
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );

  // Summary calculations
  const totalDebit = transactions
    .filter((t) => t.transactionType === "Debit")
    .reduce((sum, t) => sum + t.amount, 0);
  const totalCredit = transactions
    .filter((t) => t.transactionType === "Credit")
    .reduce((sum, t) => sum + t.amount, 0);
  const totalTransactions = transactions.length;

  const columns = [
    {
      name: "S.N",
      selector: (row, index) => (currentPage - 1) * pageSize + index + 1,
      sortable: false,
      width: "75px",
    },
    {
      name: "Invoice",
      selector: (row) => row.transactionId,
      cell: (row) => (
        <Link to={`/order/${row.transactionId}`}>{row.transactionId}</Link>
      ),
      width: "15vw",
    },
    {
      name: "Date",
      selector: (row) => row.valueDate,
      cell: (row) => formatDate(row.valueDate),
      width: "10vw",
    },

    { name: "Remarks", selector: (row) => row.remarks },
    {
      name: "Debit",
      selector: (row) => (row.transactionType === "Debit" ? row.amount : "-"),
      cell: (row) =>
        row.transactionType === "Debit" ? (
          <div className="text-end">
            <NumberWithCommas number={row.amount} />
          </div>
        ) : (
          <div className="text-end">-</div>
        ),
      width: "10vw",
      right: true,
    },
    {
      name: "Credit",
      selector: (row) => (row.transactionType === "Credit" ? row.amount : "-"),
      cell: (row) =>
        row.transactionType === "Credit" ? (
          <div className="text-end">
            <NumberWithCommas number={row.amount} />
          </div>
        ) : (
          <div className="text-end">-</div>
        ),
      width: "10vw",
      right: true,
    },
    {
      name: "Transaction By",
      selector: (row) => row.transactionBy,
      width: "10vw",
      center: true,
    },
  ];

  // Export function
  const handleExport = () => {
    if (transactions.length === 0) {
      toast.error("No data to export");
      return;
    }

    // Prepare export data
    const exportData = transactions.map((tx, index) => ({
      "S.N": index + 1,
      "Transaction ID": tx.transactionId,
      "Transaction Date": formatDate(tx.valueDate),
      // "Payment Type": tx.paymentType,
      Debit: tx.transactionType === "Debit" ? tx.amount : 0,
      Credit: tx.transactionType === "Credit" ? tx.amount : 0,
      Remarks: tx.remarks,
    }));

    // Column mapping for export
    const columnMapping = {
      "S.N": "S.N",
      "Transaction ID": "Transaction ID",
      "Transaction Date": "Transaction Date",
      // "Payment Type": "Payment Type",
      Debit: "Debit",
      Credit: "Credit",
      Remarks: "Remarks",
    };

    exportToExcel(
      exportData,
      columnMapping,
      `PaymentReport_${formatLocalDate(filters.startDate)}_to_${formatLocalDate(
        filters.endDate,
      )}.xlsx`,
    );

    toast.success("Export successful!");
  };

  return (
    <Container fluid>
      <div className="page-content">
        <BreadCrumb
          title="Payment Statement"
          pageTitle="Payment Records"
          breadcrumbItems={breadcrumbItems}
        />

        {/* Summary */}
        <Row className="mt-2">
          <Col lg={4} md={6} sm={12}>
            <SummaryCard
              title="Total Transactions"
              value={totalTransactions}
              color="#0d6efd"
              icon="bx bx-receipt"
            />
          </Col>
          <Col lg={4} md={6} sm={12}>
            <SummaryCard
              title="Total Debit"
              value={totalDebit}
              color="#28a745"
              icon="bx bx-dollar-circle"
            />
          </Col>
          <Col lg={4} md={6} sm={12}>
            <SummaryCard
              title="Total Credit"
              value={totalCredit}
              color="#17a2b8"
              icon="bx bx-loader-circle"
            />
          </Col>
        </Row>

        <div
          className="p-3 p-lg-4 mt-1"
          style={{
            background: "#ffffff",
            borderRadius: "0.5rem",
            boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
            border: "1px solid #eee",
          }}
        >
          <Row className="mb-3">
            <Col lg={5}>
              <h5 className="mb-1 fw-bold text-dark">
                Payment Statement Report{" "}
                {filters.paymentTypeId && paymentTypeData.length > 0
                  ? `(${
                      paymentTypeData.find(
                        (pt) => pt.id === filters.paymentTypeId,
                      )?.name
                    })`
                  : ""}
              </h5>
            </Col>
            <Col lg={7}>
              <Row>
                <Col md={5}>
                  <Select
                    options={paymentTypeData.map((pt) => ({
                      value: pt.id,
                      label: pt.name,
                    }))}
                    value={
                      paymentTypeData.find(
                        (pt) => pt.id === filters.paymentTypeId,
                      )
                        ? {
                            value: filters.paymentTypeId,
                            label: paymentTypeData.find(
                              (pt) => pt.id === filters.paymentTypeId,
                            ).name,
                          }
                        : null
                    }
                    onChange={(option) =>
                      handleFilterChange(option?.value || null, "paymentTypeId")
                    }
                    placeholder="Select Payment Type"
                    isClearable
                  />
                </Col>
                <Col md={5}>
                  <InputGroup>
                    <Flatpickr
                      className="form-control"
                      value={[filters.startDate, filters.endDate].filter(
                        Boolean,
                      )}
                      onChange={([start, end]) =>
                        setFilters((prev) => ({
                          ...prev,
                          startDate: start || null,
                          endDate: end || null,
                        }))
                      }
                      options={{
                        mode: "range",
                        dateFormat: "Y-m-d",
                        allowInput: false,
                      }}
                      placeholder="Select Date Range"
                    />
                    <InputGroupText style={{ cursor: "pointer" }}>
                      <i className="bx bx-calendar"></i>
                    </InputGroupText>
                  </InputGroup>
                </Col>
                <Col md={2}>
                  <Button color="success" onClick={handleExport}>
                    <i className="bx bx-export me-1"></i>Export
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>

          <hr style={{ borderColor: "#000000ff", marginTop: "0rem" }} />

          <CustomDataTable
            columns={columns}
            data={transactions}
            noHeader
            pagination={transactions.length > 0}
            striped
            highlightOnHover
            responsive
            persistTableHead
            // paginationTotalRows={totalRows}
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
            paginationDefaultPage={currentPage}
            paginationPerPage={pageSize}
            noDataComponent={
              <div className="text-center my-5 w-100">
                <i className="ri-emotion-unhappy-line display-4 text-secondary" />
                <h5 className="fw-semibold text-secondary">
                  No data Available
                </h5>
                <p className="text-muted mb-0">
                  {filters.paymentTypeId
                    ? "No transactions found for the selected payment type and date range."
                    : "Please choose a payment type to see updated availability."}
                </p>
              </div>
            }
          />
        </div>
      </div>
    </Container>
  );
};

export default PaymentReport;
